/**
 * Host context — inflomatrix.app (platform) vs {tenant}.inflomatrix.app (tenant)
 *
 * @see doc/system-completion-plan/01-target/TENANT_HOST_PATH_PLANES.md · D14
 */

import { getTenantIdFromHost, normalizeHostname } from './getTenantIdFromHost';

export type HostKind = 'platform' | 'tenant';

/** Staging canonical zone — env 누락 시 hostname에서 추론 (Vercel build safety). */
const STAGING_CANONICAL_ROOT = 'staging.inflomatrix.app';

function isLocalDevHostname(host: string): boolean {
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
}

function inferRootDomainFromHostname(host: string): string | null {
  if (host === STAGING_CANONICAL_ROOT || host.endsWith(`.${STAGING_CANONICAL_ROOT}`)) {
    return STAGING_CANONICAL_ROOT;
  }
  return null;
}

export interface HostContext {
  kind: HostKind;
  /** tenant host일 때 slug; platform이면 null */
  tenantId: string | null;
  hostname: string;
  /** env 미설정(로컬)이면 null */
  rootDomain: string | null;
}

export interface ResolveHostContextOptions {
  hostname: string;
  rootDomain: string | undefined;
  /** VITE 미설정 localhost 등 — tenant plane으로 동작할 기본 tenantId */
  devFallbackTenantId?: string;
}

/**
 * 현재 요청 host가 platform인지 tenant canonical host인지 판별한다.
 *
 * - `{tenant}.{root}` → tenant
 * - `{root}` → platform
 * - rootDomain 미설정(localhost) → devFallbackTenantId가 있으면 tenant, 없으면 platform
 */
export function resolveHostContext(options: ResolveHostContextOptions): HostContext {
  const { hostname, rootDomain, devFallbackTenantId } = options;
  const host = normalizeHostname(hostname);
  const root = (rootDomain || '').trim().toLowerCase() || inferRootDomainFromHostname(host) || null;

  if (!root) {
    if (devFallbackTenantId && isLocalDevHostname(host)) {
      return { kind: 'tenant', tenantId: devFallbackTenantId, hostname: host, rootDomain: null };
    }
    return { kind: 'platform', tenantId: null, hostname: host, rootDomain: null };
  }

  const tenantId = getTenantIdFromHost(host, root);
  if (tenantId) {
    return { kind: 'tenant', tenantId, hostname: host, rootDomain: root };
  }

  if (host === root) {
    return { kind: 'platform', tenantId: null, hostname: host, rootDomain: root };
  }

  return { kind: 'platform', tenantId: null, hostname: host, rootDomain: root };
}

/** 플랫폼 canonical URL을 조립한다 (scheme 기본 https). */
export function buildPlatformUrl(rootDomain: string, path: string, scheme = 'https'): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${scheme}://${rootDomain}${normalizedPath}`;
}

/** tenant canonical URL을 조립한다. */
export function buildTenantUrl(
  tenantId: string,
  rootDomain: string,
  path: string,
  scheme = 'https'
): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${scheme}://${tenantId}.${rootDomain}${normalizedPath}`;
}
